import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./styles.css";
import { assessEligibility, formatMoney, type EligibilityInput } from "./rules";
import type { ContentData, RuleData } from "./types";

const DATA_PATHS = {
  rules: "/data/rules.json",
  content: "/data/content.json",
  municipalities: "/data/municipalities.geojson",
  eligibleMunicipalities: "/data/eligible-municipalities.geojson",
  ineligibleMunicipalities: "/data/ineligible-municipalities.geojson",
  eligibleDeso: "/data/eligible-deso.geojson",
  places: "/data/places.geojson",
  qa: "/data/qa-report.json"
};

const ZOOM_LEVELS = [
  { level: 0, minZoom: 0 },
  { level: 1, minZoom: 5 },
  { level: 2, minZoom: 6 },
  { level: 3, minZoom: 7 },
  { level: 4, minZoom: 8 },
  { level: 5, minZoom: 9 },
  { level: 6, minZoom: 11 },
  { level: 7, minZoom: 13 }
];

const fallbackRules: RuleData = {
  retrievedAt: "2026-04-29",
  regulation: { effectiveFrom: "2026-03-18", expiresAt: "2032-08-01", applicationCloses: "2029-06-30" },
  amounts: { monthlyPremium: 1300, maxMonths: 36, totalPremium: 46800, startBonus: 18000 },
  purchase: { minPrice: 64800, maxPrice: 450000 },
  leasing: { minMonthlyCost: 1800, maxMonthlyCost: 4600 },
  incomeThresholds: {
    "En vuxen": 352000,
    "En vuxen och ett barn": 535040,
    "En vuxen och två barn": 682880,
    "En vuxen och tre barn": 830720,
    "Två vuxna": 531520,
    "Två vuxna och ett barn": 714560,
    "Två vuxna och två barn": 862400,
    "Två vuxna och tre barn": 1010240
  },
  startBonusThresholds: {
    "En vuxen": 220000,
    "En vuxen och ett barn": 334400,
    "En vuxen och två barn": 426800,
    "En vuxen och tre barn": 519200,
    "Två vuxna": 332200,
    "Två vuxna och ett barn": 446600,
    "Två vuxna och två barn": 539000,
    "Två vuxna och tre barn": 631400
  },
  ruralMunicipalities: [],
  eligibleDesoCodes: [],
  officialTestUrl: "https://www.naturvardsverket.se/bidrag/elbilspremien/",
  sources: [
    { title: "Naturvårdsverket - Elbilspremien", url: "https://www.naturvardsverket.se/bidrag/elbilspremien/", retrievedAt: "2026-04-29" },
    { title: "SFS 2025:1574", url: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/forordning-20251574-om-elbilspremie_sfs-2025-1574/", retrievedAt: "2026-04-29" }
  ]
};

const fallbackContent: ContentData = {
  retrievedAt: "2026-04-29",
  pages: [
    {
      id: "summary",
      title: "Sammanfattning",
      sections: [
        {
          heading: "Vad premien är",
          body: "Elbilspremien är ett statligt stöd för hushåll i utpekade landsbygdsområden eller DeSO-områden med begränsad kollektivtrafik. Den är inriktad på hushåll med lägre inkomster och gäller både köp och leasing av elbil.",
          sourceUrl: "https://www.naturvardsverket.se/bidrag/elbilspremien/"
        },
        {
          heading: "Tidsperiod",
          body: "Förordningen gäller från 18 mars 2026 och upphör 1 augusti 2032. Naturvårdsverket anger att ansökan är öppen till 30 juni 2029.",
          sourceUrl: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/forordning-20251574-om-elbilspremie_sfs-2025-1574/"
        }
      ]
    }
  ],
  faq: [
    {
      question: "Är detta Naturvårdsverkets test?",
      answer: "Nej. Detta är en lokal, inofficiell kontroll som hjälper dig förstå villkoren. Använd alltid Naturvårdsverkets officiella test och ansökan för skarp prövning.",
      sourceUrl: "https://www.naturvardsverket.se/bidrag/elbilspremien/"
    }
  ],
  sources: fallbackRules.sources
};

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing #app");

app.innerHTML = `
  <header class="topbar">
    <div class="brand">
      <p class="eyebrow">Inofficiell informationssida</p>
      <h1>Elbilspremien</h1>
    </div>
    <nav aria-label="Huvudnavigering">
      <a href="#karta">Karta</a>
      <a href="#test">Behörighetstest</a>
      <a href="#info">Information</a>
      <a href="#faq">FAQ</a>
    </nav>
  </header>
  <main>
    <section class="hero">
      <div>
        <p class="eyebrow">Test på en minut</p>
        <h2>Snabbtest – se om du har rätt till elbilspremie</h2>
        <p>Sök på en adress eller välj en plats i kartan.</p>
      </div>
      <div class="hero-stats" aria-label="Snabbfakta">
        <span><strong>64 800 kr</strong> max med starttillägg</span>
        <span><strong>177</strong> landsbygdskommuner</span>
        <span><strong>433</strong> områden med begränsad kollektivtrafik</span>
      </div>
    </section>
    <section class="notice">
      <strong>Detta är en inofficiell informationssida.</strong>
      Beslut fattas av Naturvårdsverket. Sidan sparar inga personuppgifter.
    </section>
    <section id="karta" class="map-shell">
      <div class="map-panel">
        <div class="section-title">
          <div>
            <p class="eyebrow">Karta</p>
            <h2>Kommuner och områden med begränsad kollektivtrafik</h2>
          </div>
        </div>
        <div class="search-row">
          <label class="sr-only" for="searchInput">Sök adress, ort, kommun eller områdeskod</label>
          <input id="searchInput" type="search" placeholder="Sök adress, ort, kommun eller områdeskod" autocomplete="off" />
          <button id="searchButton" type="button">Sök</button>
          <a class="button ghost" href="https://www.naturvardsverket.se/bidrag/elbilspremien/" target="_blank" rel="noreferrer">Officiellt test</a>
        </div>
        <div id="searchResults" class="results search-dropdown" hidden></div>
        <div id="areaResult" class="area-result neutral">
          <strong>Adresskontroll</strong>
          <span>Sök en adress eller ort för att se om platsen verkar ligga i ett stödberättigat område.</span>
        </div>
        <div class="layer-panel" aria-label="Kartlager">
          <label><input type="checkbox" data-layer-toggle="ineligible-municipalities" /> <span class="swatch outside"></span>Utanför stödmarkerade områden</label>
          <label><input type="checkbox" data-layer-toggle="eligible-municipalities" checked /> <span class="swatch rural"></span>Landsbygdskommuner</label>
          <label><input type="checkbox" data-layer-toggle="eligible-deso" checked /> <span class="swatch deso"></span>Begränsad kollektivtrafik</label>
        </div>
        <div id="map" aria-label="Karta över Sverige"></div>
      </div>
      <aside class="side-panel">
        <h3>Snabbfakta</h3>
        <dl id="facts"></dl>
      </aside>
    </section>
    <section id="test" class="content-band"></section>
    <section id="info" class="content-band"></section>
    <section id="faq" class="content-band"></section>
  </main>
`;

const map = L.map("map", { zoomControl: true }).setView([62.2, 15.2], 5);
L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
  maxZoom: 18,
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
}).addTo(map);

const layerControl = L.control.layers(undefined, undefined, { collapsed: false }).addTo(map);
const searchIndex: SearchItem[] = [];
const searchKeys = new Map<string, number>();
const dataCache = new Map<string, GeoJSON.GeoJsonObject>();
const geocodeCache = new Map<string, GeocodeResult[]>();
const mapLayers = new Map<string, MapLayerState>();
const ELIGIBILITY_STORAGE_KEY = "elbilspremien.eligibility.v1";
const MAP_SELECTION_STORAGE_KEY = "elbilspremien.mapSelection.v1";
let addressMarker: L.Marker | undefined;
let searchSelectionMarker: L.CircleMarker | undefined;

void boot();

async function boot() {
  const [rules, content] = await Promise.all([
    loadJson<RuleData>(DATA_PATHS.rules, fallbackRules),
    loadJson<ContentData>(DATA_PATHS.content, fallbackContent)
  ]);

  renderFacts(rules);
  renderEligibility(rules);
  renderInfo(content, rules);
  renderFaq(content);
  await loadMapLayers(rules);
  await buildStaticSearchIndex();
  bindLayerToggles();
  bindSearch();
  void restoreMapSelection();
}

async function loadJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const response = await fetch(url);
    if (!response.ok) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

async function loadMapLayers(rules: RuleData) {
  const status = document.querySelector("#dataStatus");
  const layers: Array<Promise<void>> = [];

  layers.push(addGeoJsonLayer("ineligible-municipalities", DATA_PATHS.ineligibleMunicipalities, "Utanför stödmarkerade områden", "#d92f1f", false, (feature) => {
    const name = getProp(feature, ["KOMMUNNAMN", "kommunnamn", "kom_namn", "name", "namn"]);
    return `<strong>${name || "Kommun"}</strong><br>Röd yta visar område utanför gröna kommuner och blå stödmarkerade områden i lokal data.`;
  }, false, { fillOpacity: 0.11, weight: 0.9 }));

  layers.push(addGeoJsonLayer("eligible-municipalities", DATA_PATHS.eligibleMunicipalities, "Berättigade landsbygdskommuner", "#18845f", true, (feature) => {
    const name = getProp(feature, ["KOMMUNNAMN", "kommunnamn", "kom_namn", "name", "namn"]);
    return `<strong>${name || "Kommun"}</strong><br>Omfattas enligt SFS 2025:1574 bilaga 1.`;
  }));

  layers.push(addGeoJsonLayer("eligible-deso", DATA_PATHS.eligibleDeso, "Områden med begränsad kollektivtrafik", "#2277d8", true, (feature) => {
    const code = getProp(feature, ["DeSO", "deso", "DESO", "deso_code", "desokod"]);
    const municipality = getProp(feature, ["KOMMUNNAMN", "kommunnamn", "kommun"]);
    return `<strong>Område ${code || ""}</strong><br>${municipality || ""}<br>Omfattas enligt SFS 2025:1574 bilaga 2.`;
  }));

  await Promise.all(layers);
  status?.classList.add("ok");
  if (status) status.textContent = searchIndex.length > 0 ? "Startlager laddade" : "Kör npm run fetch:data && npm run build:data";
}

async function addGeoJsonLayer(layerKey: string, fallbackUrl: string, label: string, color: string, addByDefault: boolean, popup: (feature: GeoJSON.Feature) => string, pointLayer = false, options: { fillOpacity?: number; weight?: number } = {}) {
  const layer = L.geoJSON(undefined, {
      style: () => ({ color, fillColor: color, fillOpacity: options.fillOpacity ?? 0.16, weight: options.weight ?? 1.4 }),
      pointToLayer: (_feature, latlng) => L.circleMarker(latlng, { radius: 5, color, fillColor: color, fillOpacity: 0.75, weight: 1 }),
      onEachFeature: (feature, featureLayer) => {
        featureLayer.bindPopup(popup(feature));
        featureLayer.on("click", (event) => updateAreaFromFeature(feature, label, event.latlng));
        const labelText =
          getProp(feature, ["KOMMUNNAMN", "kommunnamn", "kom_namn", "TATORTNAMN", "tatortnamn", "tatort", "DeSO", "deso", "desokod", "name", "namn"]) || label;
        const stableKey = `${label}:${labelText}:${getProp(feature, ["DeSO", "deso", "desokod", "KOMMUNKOD", "kommunkod", "id"]) || ""}`;
        const item = { label: String(labelText), type: label, layer: featureLayer, feature, latlng: featureCenter(feature), meta: getProp(feature, ["DeSO", "deso", "desokod", "KOMMUNKOD", "kommunkod", "id"]) };
        const existing = searchKeys.get(stableKey);
        if (existing === undefined) {
          searchKeys.set(stableKey, searchIndex.length);
          searchIndex.push(item);
        } else {
          searchIndex[existing] = item;
        }
      }
  });
  let loadedLevel = -1;
  let loadingLevel = -1;
  const load = async () => {
    const level = currentScaleLevel();
    if (loadedLevel === level || loadingLevel === level) return;
    loadingLevel = level;
    const status = document.querySelector("#dataStatus");
    if (status) status.textContent = "Laddar karta";
    try {
      const data = await fetchGeoJson(`/data/scales/${layerKey}-${level}.geojson`, fallbackUrl);
      layer.clearLayers();
      layer.addData(data);
      loadedLevel = level;
      if (status) status.textContent = "Kartan är klar";
    } catch {
      loadingLevel = -1;
      if (status) status.textContent = `Kunde inte ladda ${label}`;
    } finally {
      loadingLevel = -1;
    }
  };

  map.on("overlayadd", (event) => {
    if (event.layer === layer) void load();
  });

  if (addByDefault || pointLayer) {
    layer.addTo(map);
    await load();
  }

  layerControl.addOverlay(layer, label);
  mapLayers.set(layerKey, { layer, label, load });

  map.on("zoomend", () => {
    if (map.hasLayer(layer)) void load();
  });
}

async function fetchGeoJson(url: string, fallbackUrl: string): Promise<GeoJSON.GeoJsonObject> {
  const cached = dataCache.get(url);
  if (cached) return cached;
  let response = await fetch(url);
  if (!response.ok) response = await fetch(fallbackUrl);
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const data = (await response.json()) as GeoJSON.GeoJsonObject;
  dataCache.set(url, data);
  return data;
}

function currentScaleLevel(): number {
  const zoom = map.getZoom();
  let selected = 0;
  for (const item of ZOOM_LEVELS) {
    if (zoom >= item.minZoom) selected = item.level;
  }
  return selected;
}

function bindLayerToggles() {
  document.querySelectorAll<HTMLInputElement>("[data-layer-toggle]").forEach((toggle) => {
    const key = toggle.dataset.layerToggle || "";
    const state = mapLayers.get(key);
    if (!state) return;
    toggle.checked = map.hasLayer(state.layer);
    toggle.addEventListener("change", () => {
      if (toggle.checked) {
        state.layer.addTo(map);
        void state.load();
      } else {
        state.layer.removeFrom(map);
      }
    });
  });
}

async function buildStaticSearchIndex() {
  const [municipalities, places, desoPoints] = await Promise.all([
    loadJson<GeoJSON.FeatureCollection>(DATA_PATHS.municipalities, { type: "FeatureCollection", features: [] }),
    loadJson<GeoJSON.FeatureCollection>("/data/scales/places-7.geojson", { type: "FeatureCollection", features: [] }),
    loadJson<GeoJSON.FeatureCollection>("/data/scales/eligible-deso-0.geojson", { type: "FeatureCollection", features: [] })
  ]);

  municipalities.features.forEach((feature) => {
    const label = getProp(feature, ["KOMMUNNAMN", "kommunnamn", "kom_namn", "name", "namn"]);
    if (label) addSearchItem({ label, type: "Kommun", latlng: featureCenter(feature), meta: getProp(feature, ["id", "KOMMUNKOD", "kommunkod"]) });
  });
  places.features.forEach((feature) => {
    const label = getProp(feature, ["TATORTNAMN", "tatortnamn", "tatort", "name", "namn"]);
    if (label) addSearchItem({ label, type: "Ort", latlng: pointLatLng(feature), meta: getProp(feature, ["kommunnamn", "kommun"]) });
  });
  desoPoints.features.forEach((feature) => {
    const label = getProp(feature, ["DeSO", "deso", "DESO", "deso_code", "desokod"]);
    if (label) addSearchItem({ label, type: "Område med begränsad kollektivtrafik", latlng: pointLatLng(feature), meta: getProp(feature, ["kommunkod"]) });
  });
}

function addSearchItem(item: SearchItem) {
  const stableKey = `${item.type}:${item.label}:${item.meta || ""}`;
  const existing = searchKeys.get(stableKey);
  if (existing === undefined) {
    searchKeys.set(stableKey, searchIndex.length);
    searchIndex.push(item);
  } else {
    searchIndex[existing] = { ...searchIndex[existing], ...item };
  }
}

function dedupeSearchMatches(items: SearchItem[]): SearchItem[] {
  const hasEligibleMunicipality = new Set(
    items
      .filter((item) => item.type.includes("landsbygdskommun"))
      .map((item) => normalizedSearchLabel(item.label))
  );
  return items.filter((item) => !(item.type === "Kommun" && hasEligibleMunicipality.has(normalizedSearchLabel(item.label))));
}

function normalizedSearchLabel(value: string): string {
  return value.trim().toLocaleLowerCase("sv-SE");
}

function pointLatLng(feature: GeoJSON.Feature): L.LatLngExpression | undefined {
  if (feature.geometry?.type !== "Point") return undefined;
  const [lon, lat] = feature.geometry.coordinates;
  return [lat, lon];
}

function featureCenter(feature: GeoJSON.Feature): L.LatLngExpression | undefined {
  const points: number[][] = [];
  collectLonLat(feature.geometry, points);
  if (!points.length) return undefined;
  const sum = points.reduce((acc, point) => [acc[0] + point[0], acc[1] + point[1]], [0, 0]);
  return [sum[1] / points.length, sum[0] / points.length];
}

function collectLonLat(geometry: GeoJSON.Geometry | null, points: number[][]) {
  if (!geometry) return;
  if (geometry.type === "GeometryCollection") {
    geometry.geometries.forEach((item) => collectLonLat(item, points));
    return;
  }
  collectCoords(geometry.coordinates, points);
}

function collectCoords(value: GeoJSON.Position | GeoJSON.Position[] | GeoJSON.Position[][] | GeoJSON.Position[][][], points: number[][]) {
  if (!Array.isArray(value)) return;
  if (typeof value[0] === "number") {
    points.push(value as number[]);
    return;
  }
  (value as Array<GeoJSON.Position | GeoJSON.Position[] | GeoJSON.Position[][]>).forEach((item) => collectCoords(item, points));
}

function getProp(feature: GeoJSON.Feature, keys: string[]): string | undefined {
  const props = feature.properties || {};
  for (const key of keys) {
    const value = props[key];
    if (value !== undefined && value !== null && value !== "") return String(value);
  }
  return undefined;
}

function bindSearch() {
  const input = document.querySelector<HTMLInputElement>("#searchInput");
  const button = document.querySelector<HTMLButtonElement>("#searchButton");
  const results = document.querySelector<HTMLDivElement>("#searchResults");
  const run = async (): Promise<SearchItem[] | undefined> => {
    const query = input?.value.trim().toLowerCase() || "";
    if (!results) return;
    clearAddressMarker();
    clearSearchSelectionMarker();
    if (!query) {
      results.hidden = true;
      results.innerHTML = "";
      return [];
    }
    results.hidden = false;
    const matches = dedupeSearchMatches(searchIndex
      .filter((item) => `${item.label} ${item.meta || ""}`.toLowerCase().includes(query))
    ).slice(0, 12);
    const canGeocode = looksLikeAddress(query) || query.length >= 4;
    const geocodeButton = canGeocode
      ? `<button type="button" class="result-check" data-geocode="true"><strong>Sök adress/plats via geokodning</strong><span>${escapeHtml(input?.value.trim() || "")} · kontrolleras när du väljer</span></button>`
      : "";
    if (matches.length === 0 && !canGeocode) {
      results.innerHTML = `Inga lokala träffar. Skriv en adress eller använd <a href="${fallbackRules.officialTestUrl}" target="_blank" rel="noreferrer">Naturvårdsverkets test</a>.`;
      return [];
    }
    results.innerHTML = matches
      .map((item, index) => `<button type="button" class="${searchResultClass(item)}" data-result="${index}"><strong>${item.label}</strong><span>${item.type}${item.meta ? ` · ${item.meta}` : ""}</span></button>`)
      .join("") + geocodeButton;
    results.querySelectorAll<HTMLButtonElement>("button").forEach((el) => {
      el.addEventListener("click", () => {
        if (el.dataset.geocode) {
          void geocodeAndRender(input?.value.trim() || "", results);
          return;
        }
        selectSearchMatch(matches[Number(el.dataset.result)]);
      });
    });
    return matches;
  };
  const autocomplete = debounce(() => {
    void run();
  }, 180);
  button?.addEventListener("click", () => {
    void run().then((matches) => {
      const query = input?.value.trim() || "";
      if (!matches?.length && query) void geocodeAndRender(query, results as HTMLDivElement);
    });
  });
  input?.addEventListener("input", autocomplete);
  input?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void run().then((matches) => {
        const query = input?.value.trim() || "";
        if (!matches?.length && query && results) void geocodeAndRender(query, results);
      });
    }
  });
}

map.on("click", (event) => {
  void reverseGeocodeMapPoint(event.latlng.lat, event.latlng.lng);
});

function searchResultClass(item: { label: string; type: string }): string {
  if (item.type.includes("begränsad")) return "result-ok";
  if (item.type === "Kommun") return fallbackRules.ruralMunicipalities.includes(item.label) ? "result-ok" : "result-check";
  if (item.type === "Ort") return "result-check";
  return "result-check";
}

function isMunicipalitySearchMatch(item: { type: string }): boolean {
  return item.type === "Kommun" || item.type.includes("kommun");
}

function areaResultClass(area: AreaAssessment): string {
  if (area.status === "yes") return "result-ok";
  if (area.status === "no") return "result-no";
  return "result-check";
}

function selectSearchMatch(match: SearchItem) {
  const input = document.querySelector<HTMLInputElement>("#searchInput");
  const results = document.querySelector<HTMLDivElement>("#searchResults");
  clearAddressMarker();
  clearSearchSelectionMarker();
  if (input) input.value = match.label;
  if (results) results.hidden = true;
  if (match.layer && "getBounds" in match.layer) {
    const bounds = (match.layer as L.GeoJSON).getBounds?.();
    if (bounds?.isValid()) map.fitBounds(bounds.pad(0.2));
    if (isMunicipalitySearchMatch(match) && match.latlng) {
      const latlng = L.latLng(match.latlng);
      searchSelectionMarker = L.circleMarker(latlng, {
        radius: 10,
        color: "#0f172a",
        fillColor: "#fbbf24",
        fillOpacity: 0.85,
        opacity: 1,
        weight: 3
      }).addTo(map);
      persistMapSelection({ kind: "search", lat: latlng.lat, lng: latlng.lng, label: match.label, type: match.type, meta: match.meta });
      void updateAreaFromLocalSearch(match, latlng);
    } else if (match.feature) {
      updateAreaFromFeature(match.feature, match.type);
    }
  } else if (match.latlng) {
    const latlng = L.latLng(match.latlng);
    searchSelectionMarker = L.circleMarker(latlng, {
      radius: 10,
      color: "#0f172a",
      fillColor: "#fbbf24",
      fillOpacity: 0.85,
      opacity: 1,
      weight: 3
    }).addTo(map);
    searchSelectionMarker
      .bindPopup(`<strong>${escapeHtml(match.label)}</strong><br>${escapeHtml(match.type)}${match.meta ? `<br>${escapeHtml(match.meta)}` : ""}`)
      .openPopup();
    map.setView(latlng, match.type.includes("begränsad") ? 11 : 10);
    persistMapSelection({ kind: "search", lat: latlng.lat, lng: latlng.lng, label: match.label, type: match.type, meta: match.meta });
    void updateAreaFromLocalSearch(match, latlng);
  }
  if (match.layer && "openPopup" in match.layer) (match.layer as L.Layer & { openPopup: () => void }).openPopup();
}

async function geocodeAndRender(query: string, results: HTMLDivElement) {
  if (!query) return;
  results.innerHTML = `<span class="search-status">Söker adress...</span>`;
  try {
    const found = await geocodeSweden(query);
    if (!found.length) {
      results.innerHTML = `Ingen adress-/platsträff hittades. Prova med gata, nummer och kommun.`;
      return;
    }
    const assessed = await Promise.all(found.map(async (item) => ({ item, area: await evaluateArea(item.lat, item.lon) })));
    results.innerHTML = assessed
      .map(({ item, area }, index) => `<button type="button" class="${areaResultClass(area)}" data-geocode-result="${index}"><strong>${escapeHtml(item.name)}</strong><span>${escapeHtml(area.summary)}</span></button>`)
      .join("");
    results.querySelectorAll<HTMLButtonElement>("button").forEach((el) => {
      el.addEventListener("click", () => void zoomToGeocodeResult(assessed[Number(el.dataset.geocodeResult)].item));
    });
  } catch {
    results.innerHTML = `Adressökningen kunde inte nå geokodningstjänsten. Lokala träffar fungerar fortfarande.`;
  }
}

async function geocodeSweden(query: string): Promise<GeocodeResult[]> {
  const key = query.trim().toLowerCase();
  const cached = geocodeCache.get(key);
  if (cached) return cached;
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("q", query);
  url.searchParams.set("countrycodes", "se");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("limit", "6");
  url.searchParams.set("accept-language", "sv");
  const response = await fetch(url.toString(), {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const json = (await response.json()) as Array<Record<string, string | string[]>>;
  const results = json.map((item) => ({
    name: String(item.display_name || "Adress"),
    type: `${item.category || "plats"} ${item.type || ""}`.trim(),
    lat: Number(item.lat),
    lon: Number(item.lon),
    boundingbox: Array.isArray(item.boundingbox) ? item.boundingbox.map(Number) : undefined
  })).filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon));
  geocodeCache.set(key, results);
  return results;
}

async function reverseGeocodeMapPoint(lat: number, lon: number) {
  map.closePopup();
  clearAddressMarker();
  clearSearchSelectionMarker();
  const area = await evaluateArea(lat, lon);
  let name = `Vald punkt ${lat.toFixed(5)}, ${lon.toFixed(5)}`;
  try {
    const url = new URL("https://nominatim.openstreetmap.org/reverse");
    url.searchParams.set("format", "jsonv2");
    url.searchParams.set("lat", String(lat));
    url.searchParams.set("lon", String(lon));
    url.searchParams.set("zoom", "16");
    url.searchParams.set("addressdetails", "1");
    url.searchParams.set("accept-language", "sv");
    const response = await fetch(url.toString(), { headers: { Accept: "application/json" } });
    if (response.ok) {
      const json = (await response.json()) as Record<string, string>;
      if (json.display_name) name = json.display_name;
    }
  } catch {
    // Reverse geocoding is helpful, but area assessment still works without it.
  }

  const latLng: L.LatLngExpression = [lat, lon];
  addressMarker = L.marker(latLng, {
    icon: selectedPointIcon(),
    zIndexOffset: 1000
  }).addTo(map);
  updateAreaResult(area, name);
  persistMapSelection({ kind: "address", lat, lng: lon, label: name, summary: area.summary });
  addressMarker.bindPopup(`<strong>${escapeHtml(name)}</strong><br>${escapeHtml(area.summary)}`).openPopup();
}

function selectedPointIcon() {
  return L.divIcon({
    className: "selected-point-marker",
    html: "<span></span>",
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -13]
  });
}

function clearAddressMarker() {
  if (!addressMarker) return;
  addressMarker.removeFrom(map);
  addressMarker = undefined;
}

function clearSearchSelectionMarker() {
  if (!searchSelectionMarker) return;
  searchSelectionMarker.removeFrom(map);
  searchSelectionMarker = undefined;
}

function persistMapSelection(selection: StoredMapSelection) {
  localStorage.setItem(MAP_SELECTION_STORAGE_KEY, JSON.stringify(selection));
}

async function restoreMapSelection() {
  const raw = localStorage.getItem(MAP_SELECTION_STORAGE_KEY);
  if (!raw) return;
  try {
    const selection = JSON.parse(raw) as StoredMapSelection;
    if (!Number.isFinite(selection.lat) || !Number.isFinite(selection.lng)) return;
    const latlng = L.latLng(selection.lat, selection.lng);
    if (selection.kind === "search") {
      searchSelectionMarker = L.circleMarker(latlng, {
        radius: 10,
        color: "#0f172a",
        fillColor: "#fbbf24",
        fillOpacity: 0.85,
        opacity: 1,
        weight: 3
      }).addTo(map);
      searchSelectionMarker.bindPopup(`<strong>${escapeHtml(selection.label)}</strong><br>${escapeHtml(selection.type || "Vald plats")}${selection.meta ? `<br>${escapeHtml(selection.meta)}` : ""}`);
      return;
    }

    addressMarker = L.marker(latlng, { icon: selectedPointIcon(), zIndexOffset: 1000 }).addTo(map);
    const area = selection.summary ? undefined : await evaluateArea(selection.lat, selection.lng);
    addressMarker.bindPopup(`<strong>${escapeHtml(selection.label)}</strong>${selection.summary || area?.summary ? `<br>${escapeHtml(selection.summary || area?.summary || "")}` : ""}`);
  } catch {
    localStorage.removeItem(MAP_SELECTION_STORAGE_KEY);
  }
}

async function zoomToGeocodeResult(result: GeocodeResult) {
  clearAddressMarker();
  clearSearchSelectionMarker();
  const latLng: L.LatLngExpression = [result.lat, result.lon];
  if (!addressMarker) {
    addressMarker = L.marker(latLng, { icon: selectedPointIcon(), zIndexOffset: 1000 }).addTo(map);
  } else {
    addressMarker.setLatLng(latLng);
  }
  const area = await evaluateArea(result.lat, result.lon);
  updateAreaResult(area, result.name);
  persistMapSelection({ kind: "address", lat: result.lat, lng: result.lon, label: result.name, summary: area.summary });
  addressMarker.bindPopup(`<strong>${escapeHtml(result.name)}</strong><br>${escapeHtml(area.summary)}<br>Kontrollera alltid hos Naturvårdsverket.`).openPopup();
  if (result.boundingbox && result.boundingbox.length === 4) {
    const [south, north, west, east] = result.boundingbox;
    map.fitBounds([[south, west], [north, east]], { maxZoom: 16, padding: [24, 24] });
  } else {
    map.setView(latLng, looksLikeAddress(result.name) ? 16 : 12);
  }
}

function looksLikeAddress(value: string): boolean {
  return /\d/.test(value) && /[a-zåäö]{2,}/i.test(value);
}

async function evaluateArea(lat: number, lon: number): Promise<AreaAssessment> {
  const point: [number, number] = [lon, lat];
  const [eligibleMunicipalities, eligibleDeso, municipalities] = await Promise.all([
    loadJson<GeoJSON.FeatureCollection>(DATA_PATHS.eligibleMunicipalities, { type: "FeatureCollection", features: [] }),
    loadJson<GeoJSON.FeatureCollection>(DATA_PATHS.eligibleDeso, { type: "FeatureCollection", features: [] }),
    loadJson<GeoJSON.FeatureCollection>(DATA_PATHS.municipalities, { type: "FeatureCollection", features: [] })
  ]);

  const deso = eligibleDeso.features.find((feature) => geometryContainsPoint(feature.geometry, point));
  if (deso) {
    return {
      status: "yes",
      summary: `Platsen verkar ligga i område ${getProp(deso, ["desokod", "DeSO", "deso"]) || ""} med begränsad kollektivtrafik, bilaga 2.`,
      detail: "Området är markerat som stödberättigat område med begränsad kollektivtrafik i lokal SFS/SCB-data."
    };
  }

  const rural = eligibleMunicipalities.features.find((feature) => geometryContainsPoint(feature.geometry, point));
  if (rural) {
    return {
      status: "yes",
      summary: `Platsen verkar ligga i ${getProp(rural, ["kom_namn", "kommunnamn", "KOMMUNNAMN"]) || "en landsbygdskommun"}, bilaga 1.`,
      detail: "Kommunen är markerad som stödberättigad landsbygdskommun i lokal SFS-data."
    };
  }

  const municipality = municipalities.features.find((feature) => geometryContainsPoint(feature.geometry, point));
  if (municipality) {
    return {
      status: "no",
      summary: `Platsen verkar ligga i ${getProp(municipality, ["kom_namn", "kommunnamn", "KOMMUNNAMN"]) || "en kommun"} utanför landsbygdsområde eller område med begränsad kollektivtrafik.`,
      detail: "Det kan fortfarande finnas gränsfall. Kontrollera exakt adress hos Naturvårdsverket."
    };
  }

  return {
    status: "unknown",
    summary: "Platsen kunde inte matchas mot lokala kommuner eller kartområden.",
    detail: "Använd Naturvårdsverkets officiella test för skarp adresskontroll."
  };
}

function updateAreaResult(area: AreaAssessment, placeName: string) {
  const target = document.querySelector<HTMLDivElement>("#areaResult");
  if (target) {
    target.className = `area-result ${area.status}`;
    target.innerHTML = `<strong>${escapeHtml(area.status === "yes" ? "Troligen stödberättigat område" : area.status === "no" ? "Troligen inte stödberättigat område" : "Behöver kontrolleras")}</strong><span>${escapeHtml(area.summary)}</span><small>${escapeHtml(area.detail)} Källa: lokal data, ingen personuppgift sparas.</small>`;
  }
  const form = document.querySelector<HTMLFormElement>("#eligibilityForm");
  const select = form?.elements.namedItem("inEligibleArea") as HTMLSelectElement | null;
  const address = form?.elements.namedItem("address") as HTMLInputElement | null;
  if (select) select.value = area.status === "yes" ? "yes" : area.status === "no" ? "no" : "unknown";
  if (address) address.value = placeName;
  if (form) {
    persistEligibilityForm(form);
    updateTestLiveState(fallbackRules, form);
  }
  scheduleScrollToTest();
}

async function updateAreaFromLocalSearch(match: { label: string; type: string; meta?: string }, latlng?: L.LatLng) {
  if (match.type === "Ort") {
    if (latlng) {
      const area = await evaluateArea(latlng.lat, latlng.lng);
      updateAreaResult(area.status === "yes" ? {
        ...area,
        summary: `${match.label}${match.meta ? `, ${match.meta}` : ""} har en ortpunkt som ligger i stödberättigat område. ${area.summary}`,
        detail: `${area.detail} Sök ändå exakt adress om platsen ligger nära en områdesgräns.`
      } : {
        status: "unknown",
        summary: `${match.label}${match.meta ? `, ${match.meta}` : ""} är en ortträff. Exakt adress behöver kontrolleras mot stödberättigad kommun eller område.`,
        detail: "Ortpunkten ger inte ett säkert stödberättigat resultat i lokal data. Sök helst full adress för säkrare bedömning."
      }, `${match.label}${match.meta ? `, ${match.meta}` : ""}`);
      return;
    }

    updateAreaResult({
      status: "unknown",
      summary: `${match.label}${match.meta ? `, ${match.meta}` : ""} är en ortträff. Exakt adress behöver kontrolleras mot stödberättigad kommun eller område.`,
      detail: "Orter kan ligga nära gränser eller i flera områden. Sök helst full adress för säkrare bedömning."
    }, `${match.label}${match.meta ? `, ${match.meta}` : ""}`);
    return;
  }

  if (isMunicipalitySearchMatch(match)) {
    const isRural = fallbackRules.ruralMunicipalities.includes(match.label) || match.type.includes("landsbygd");
    if (latlng && !isRural) {
      const area = await evaluateArea(latlng.lat, latlng.lng);
      updateAreaResult({
        status: "unknown",
        summary: `${match.label} är en kommunträff. Markeringen används bara som ungefärlig kontrollpunkt: ${area.summary}`,
        detail: "Hela kommunen är inte markerad som landsbygdskommun i lokal data. Sök full adress för säkrare bedömning, särskilt om platsen kan ligga i ett stödberättigat DeSO-område."
      }, match.label);
      return;
    }

    updateAreaResult({
      status: isRural ? "yes" : "unknown",
      summary: isRural ? `${match.label} är markerad som stödberättigad landsbygdskommun.` : `${match.label} är en kommunträff. Exakt adress kan ändå behöva kontrolleras.`,
      detail: isRural ? "Kommunen finns i bilaga 1." : "Sök full adress för att kontrollera om den ligger i ett stödberättigat område med begränsad kollektivtrafik."
    }, match.label);
  }
}

function updateAreaFromFeature(feature: GeoJSON.Feature, layerLabel: string, clickedLatLng?: L.LatLng) {
  const municipality = getProp(feature, ["kom_namn", "kommunnamn", "KOMMUNNAMN", "kommun"]);
  const areaCode = getProp(feature, ["desokod", "DeSO", "deso", "DESO"]);
  const markerLabel = areaCode ? `Område ${areaCode}${municipality ? `, ${municipality}` : ""}` : municipality || layerLabel;
  if (clickedLatLng) markSelectedMapPoint(clickedLatLng, markerLabel, layerLabel);

  if (layerLabel.includes("begränsad")) {
    updateAreaResult({
      status: "yes",
      summary: `Du klickade på område ${areaCode || ""} med begränsad kollektivtrafik.`,
      detail: "Området omfattas enligt SFS 2025:1574 bilaga 2. Exakt bostadsadress bör ändå kontrolleras hos Naturvårdsverket."
    }, areaCode ? `Område ${areaCode}${municipality ? `, ${municipality}` : ""}` : "Område med begränsad kollektivtrafik");
    return;
  }

  if (layerLabel.includes("landsbygd")) {
    updateAreaResult({
      status: "yes",
      summary: `${municipality || "Kommunen"} är markerad som stödberättigad landsbygdskommun.`,
      detail: "Kommunen omfattas enligt SFS 2025:1574 bilaga 1."
    }, municipality || "Landsbygdskommun");
    return;
  }

  if (layerLabel.includes("Alla kommuner")) {
    const isRural = Boolean(municipality && fallbackRules.ruralMunicipalities.includes(municipality));
    updateAreaResult({
      status: isRural ? "yes" : "unknown",
      summary: isRural ? `${municipality} är markerad som stödberättigad landsbygdskommun.` : `${municipality || "Kommunen"} är en kommunträff. Exakt adress kan behöva kontrolleras mot områden med begränsad kollektivtrafik.`,
      detail: isRural ? "Kommunen finns i bilaga 1." : "Slå på lagret för begränsad kollektivtrafik eller sök full adress."
    }, municipality || "Kommun");
  }
}

function markSelectedMapPoint(latlng: L.LatLng, label: string, type: string) {
  map.closePopup();
  clearAddressMarker();
  clearSearchSelectionMarker();
  addressMarker = L.marker(latlng, {
    icon: selectedPointIcon(),
    zIndexOffset: 1000
  }).addTo(map);
  persistMapSelection({ kind: "address", lat: latlng.lat, lng: latlng.lng, label, summary: type });
}

function geometryContainsPoint(geometry: GeoJSON.Geometry | null, point: [number, number]): boolean {
  if (!geometry) return false;
  if (geometry.type === "Polygon") return polygonContainsPoint(geometry.coordinates, point);
  if (geometry.type === "MultiPolygon") return geometry.coordinates.some((polygon) => polygonContainsPoint(polygon, point));
  if (geometry.type === "GeometryCollection") return geometry.geometries.some((item) => geometryContainsPoint(item, point));
  return false;
}

function polygonContainsPoint(rings: GeoJSON.Position[][], point: [number, number]): boolean {
  if (!rings.length || !ringContainsPoint(rings[0], point)) return false;
  return !rings.slice(1).some((ring) => ringContainsPoint(ring, point));
}

function ringContainsPoint(ring: GeoJSON.Position[], point: [number, number]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects = yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function debounce(callback: () => void, wait: number) {
  let timer: number | undefined;
  return () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(callback, wait);
  };
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[char] || char);
}

type GeocodeResult = {
  name: string;
  type: string;
  lat: number;
  lon: number;
  boundingbox?: number[];
};

type SearchItem = {
  label: string;
  type: string;
  layer?: L.Layer;
  feature?: GeoJSON.Feature;
  latlng?: L.LatLngExpression;
  meta?: string;
};

type StoredMapSelection = {
  kind: "address" | "search";
  lat: number;
  lng: number;
  label: string;
  type?: string;
  meta?: string;
  summary?: string;
};

type MapLayerState = {
  layer: L.GeoJSON;
  label: string;
  load: () => Promise<void>;
};

type AreaAssessment = {
  status: "yes" | "no" | "unknown";
  summary: string;
  detail: string;
};

let scrollToTestTimer: number | undefined;
let scrollAnimationFrame: number | undefined;

function scheduleScrollToTest() {
  window.clearTimeout(scrollToTestTimer);
  scrollToTestTimer = window.setTimeout(() => scrollToElement("#test", 1050), 850);
}

function scrollToElement(selector: string, durationMs: number) {
  const target = document.querySelector<HTMLElement>(selector);
  if (!target) return;

  const topbar = document.querySelector<HTMLElement>(".topbar");
  const offset = (topbar?.getBoundingClientRect().height || 0) + 12;
  const startY = window.scrollY;
  const targetY = Math.max(0, target.getBoundingClientRect().top + window.scrollY - offset);
  const distance = targetY - startY;

  if (Math.abs(distance) < 4) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo({ top: targetY });
    return;
  }

  if (scrollAnimationFrame) window.cancelAnimationFrame(scrollAnimationFrame);
  const startTime = window.performance.now();
  const easeInOut = (progress: number) => progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / durationMs, 1);
    window.scrollTo({ top: startY + distance * easeInOut(progress) });
    if (progress < 1) scrollAnimationFrame = window.requestAnimationFrame(step);
  };

  scrollAnimationFrame = window.requestAnimationFrame(step);
}

function renderFacts(rules: RuleData) {
  const facts = document.querySelector("#facts");
  if (!facts) return;
  facts.innerHTML = `
    <dt>Premie</dt><dd>${formatMoney(rules.amounts.monthlyPremium)} per månad i högst ${rules.amounts.maxMonths} månader</dd>
    <dt>Starttillägg</dt><dd>${formatMoney(rules.amounts.startBonus)} vid låg inkomst</dd>
    <dt>Totalt</dt><dd>${formatMoney(rules.amounts.totalPremium)} i premie plus ${formatMoney(rules.amounts.startBonus)} i starttillägg</dd>
    <dt>Pris</dt><dd>${formatMoney(rules.purchase.minPrice)}-${formatMoney(rules.purchase.maxPrice)}</dd>
    <dt>Månadskostnad</dt><dd>${formatMoney(rules.leasing.minMonthlyCost)}-${formatMoney(rules.leasing.maxMonthlyCost)} vid leasing</dd>
  `;
}

function renderEligibility(rules: RuleData) {
  const target = document.querySelector("#test");
  if (!target) return;
  const householdOptions = Object.keys(rules.incomeThresholds).map((key) => `<option value="${key}">${key} - under ${formatMoney(rules.incomeThresholds[key])}</option>`).join("");
  target.innerHTML = `
    <div class="section-title"><div><p class="eyebrow">Lokalt test</p><h2>Behörighetstest</h2></div></div>
    <div id="testProgress" class="test-progress">
      <strong>0 av 4 delar klara</strong>
      <span>Fyll i plats, hushåll, bil och kontrollfrågor.</span>
    </div>
    <form id="eligibilityForm" class="test-grid wizard">
      <fieldset>
        <legend><span>1</span>Plats</legend>
        <input type="hidden" name="inEligibleArea" value="unknown" />
        <label>Adress, kommun eller valt område <input name="address" readonly data-jump-map="true" placeholder="Välj i kartan eller sök adress ovan" /></label>
      </fieldset>
      <fieldset>
        <legend><span>2</span>Hushåll</legend>
        <label>Hushållstyp <select name="householdType">${householdOptions}</select></label>
        <label>Hushållsinkomst <input name="householdIncome" type="number" min="0" step="1000" placeholder="Fyll i kronor per år" /></label>
        ${selectField("paysStateTax", "Betalar någon vuxen statlig inkomstskatt?", [["no", "Nej"], ["yes", "Ja"]])}
        ${selectField("recentEvOrHybrid", "Elbil eller laddhybrid i hushållet senaste 12 månaderna?", [["no", "Nej"], ["yes", "Ja"]])}
      </fieldset>
      <fieldset>
        <legend><span>3</span>Bil och avtal</legend>
        ${selectField("acquisition", "Köp eller leasing?", [["purchase", "Köp"], ["leasing", "Leasing"]])}
        <label id="vehicleCostLabel">Pris <input name="vehicleCost" type="number" min="0" step="100" placeholder="Fyll i bilens pris i kronor" /></label>
      </fieldset>
      <fieldset>
        <legend><span>4</span>Kontroller</legend>
        ${selectField("ageAndIdOk", "Är sökanden minst 18 år?", [["yes", "Ja"], ["no", "Nej"]])}
        ${selectField("hasSwedishPersonalNumber", "Har sökanden svenskt personnummer?", [["yes", "Ja"], ["no", "Nej"]])}
        ${selectField("protectedRegistration", "Har sökanden skyddad folkbokföring?", [["no", "Nej"], ["yes", "Ja"]])}
        ${selectField("debtsOrOwnershipIssues", "Finns fordonsrelaterade skulder eller oklar ägarhistorik?", [["no", "Nej"], ["yes", "Ja"]])}
      </fieldset>
      <details class="advanced-checks">
        <summary>Avancerad kontroll enligt SFS 2025:1574</summary>
        <div class="advanced-grid">
          <fieldset>
            <legend><span>A</span>Fordonets status</legend>
            ${selectField("carIsElectricClass", "Är bilen en personbil klass I i utsläppsklass El?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("carIsRegisteredActive", "Är bilen påställd och utan körförbud?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("carNoPreviousPremium", "Har bilen inte tidigare gett elbilspremie till någon i ditt hushåll?", [["yes", "Ja"], ["no", "Nej"]])}
          </fieldset>
          <fieldset>
            <legend><span>B</span>Ägande och användning</legend>
            ${selectField("registeredOwnerOrHolder", "Är/blir du registrerad ägare eller innehavare i vägtrafikregistret?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("notOwnedByHouseholdRelatedCompany", "Har bilen inte tidigare ägts av hushåll, närstående eller närstående företag?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("notBusinessUse", "Ska bilen inte användas i näringsverksamhet?", [["yes", "Ja"], ["no", "Nej"]])}
          </fieldset>
          <fieldset>
            <legend><span>C</span>Ansökan</legend>
            ${selectField("applicationBeforeDeadline", "Skickas ansökan senast 30 juni 2029?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("recentRegistrationOk", "Om bilen redan är köpt/leasad: registrerades du tidigast två månader före ansökan?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("noOtherOngoingHouseholdCase", "Har ingen i hushållet ett annat pågående ärende om elbilspremie?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("firstPremiumForApplicant", "Har du inte tidigare fått elbilspremie?", [["yes", "Ja"], ["no", "Nej"]])}
          </fieldset>
          <fieldset>
            <legend><span>D</span>Villkor efter beslut</legend>
            ${selectField("sameAddressFourMonths", "Kan du bo kvar på samma stödberättigade adress minst fyra månader efter ansökan?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("eligibleAreaTwelveMonths", "Kan du bo kvar i stödberättigat område minst tolv månader efter ansökan?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("registeredForTwelveMonths", "Kan du vara registrerad ägare/innehavare i minst tolv månader?", [["yes", "Ja"], ["no", "Nej"]])}
          </fieldset>
          <fieldset>
            <legend><span>E</span>Handlingar och kontroll</legend>
            ${selectField("truthfulApplication", "Kan du lämna försäkran på heder och samvete om pris, ägande och användning?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("canProvideDocuments", "Kan du lämna handlingar/uppgifter om Naturvårdsverket begär det?", [["yes", "Ja"], ["no", "Nej"]])}
            ${selectField("acceptEuControls", "Kan du acceptera de kontrollvillkor som kan följa av beslutet?", [["yes", "Ja"], ["no", "Nej"]])}
          </fieldset>
        </div>
        <p class="source">Sammanfattat från SFS 2025:1574, särskilt 4-17, 21-28 och 31-34 §§. Det här är ett stöd för egen kontroll, inte myndighetsbeslut.</p>
      </details>
      <div class="form-actions">
        <button type="button" class="ghost" id="resetEligibility">Rensa mina svar</button>
        <a class="button ghost" href="${rules.officialTestUrl}" target="_blank" rel="noreferrer">Kontrollera hos Naturvårdsverket</a>
      </div>
    </form>
    <div id="assessment" class="assessment">Fyll i formuläret och kör en lokal bedömning. Inget skickas iväg.</div>
    <p class="test-footnote"><strong>Observera:</strong> detta är en inofficiell förhandsbedömning, inte ett beslut. Dina svar sparas bara i den här webbläsaren.</p>
  `;
  const acquisition = document.querySelector<HTMLSelectElement>('#eligibilityForm select[name="acquisition"]');
  const vehicleCostLabel = document.querySelector<HTMLLabelElement>("#vehicleCostLabel");
  const formElement = document.querySelector<HTMLFormElement>("#eligibilityForm");
  acquisition?.addEventListener("change", () => {
    updateVehicleCostLabel();
  });
  if (formElement) {
    restoreEligibilityForm(formElement);
    updateVehicleCostLabel();
    updateTestLiveState(rules, formElement);
    formElement.addEventListener("input", () => {
      persistEligibilityForm(formElement);
      updateTestLiveState(rules, formElement);
    });
    formElement.addEventListener("change", () => {
      updateVehicleCostLabel();
      persistEligibilityForm(formElement);
      updateTestLiveState(rules, formElement);
    });
  }
  document.querySelectorAll<HTMLElement>("[data-jump-map]").forEach((element) => {
    element.addEventListener("click", () => {
      document.querySelector("#karta")?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => document.querySelector<HTMLInputElement>("#searchInput")?.focus(), 350);
    });
  });
  document.querySelector<HTMLButtonElement>("#resetEligibility")?.addEventListener("click", () => {
    localStorage.removeItem(ELIGIBILITY_STORAGE_KEY);
    localStorage.removeItem(MAP_SELECTION_STORAGE_KEY);
    clearAddressMarker();
    clearSearchSelectionMarker();
    formElement?.reset();
    updateVehicleCostLabel();
    if (formElement) updateTestLiveState(rules, formElement);
  });
  function updateVehicleCostLabel() {
    const vehicleCost = document.querySelector<HTMLInputElement>('#eligibilityForm input[name="vehicleCost"]');
    if (vehicleCostLabel && acquisition) vehicleCostLabel.childNodes[0].textContent = acquisition.value === "leasing" ? "Månadskostnad " : "Pris ";
    if (vehicleCost && acquisition) vehicleCost.placeholder = acquisition.value === "leasing" ? "Fyll i kronor per månad" : "Fyll i bilens pris i kronor";
  }
}

function selectField(name: string, label: string, options: Array<[string, string]>) {
  return `<label>${label}<select name="${name}">${options.map(([value, text]) => `<option value="${value}">${text}</option>`).join("")}</select></label>`;
}

function readEligibilityForm(formElement: HTMLFormElement): EligibilityInput {
  const form = new FormData(formElement);
  return {
    inEligibleArea: String(form.get("inEligibleArea")) as EligibilityInput["inEligibleArea"],
    householdType: String(form.get("householdType")),
    householdIncome: Number(form.get("householdIncome") || 0),
      paysStateTax: String(form.get("paysStateTax")) as EligibilityInput["paysStateTax"],
      recentEvOrHybrid: String(form.get("recentEvOrHybrid")) as EligibilityInput["recentEvOrHybrid"],
      acquisition: String(form.get("acquisition")) as EligibilityInput["acquisition"],
      vehicleCost: Number(form.get("vehicleCost") || 0),
      ageAndIdOk: String(form.get("ageAndIdOk")) as EligibilityInput["ageAndIdOk"],
      hasSwedishPersonalNumber: String(form.get("hasSwedishPersonalNumber")) as EligibilityInput["hasSwedishPersonalNumber"],
      protectedRegistration: String(form.get("protectedRegistration")) as EligibilityInput["protectedRegistration"],
      debtsOrOwnershipIssues: String(form.get("debtsOrOwnershipIssues")) as EligibilityInput["debtsOrOwnershipIssues"],
      carIsElectricClass: form.get("carIsElectricClass") as EligibilityInput["carIsElectricClass"],
      carIsRegisteredActive: form.get("carIsRegisteredActive") as EligibilityInput["carIsRegisteredActive"],
      carNoPreviousPremium: form.get("carNoPreviousPremium") as EligibilityInput["carNoPreviousPremium"],
      registeredOwnerOrHolder: form.get("registeredOwnerOrHolder") as EligibilityInput["registeredOwnerOrHolder"],
      notOwnedByHouseholdRelatedCompany: form.get("notOwnedByHouseholdRelatedCompany") as EligibilityInput["notOwnedByHouseholdRelatedCompany"],
      notBusinessUse: form.get("notBusinessUse") as EligibilityInput["notBusinessUse"],
      applicationBeforeDeadline: form.get("applicationBeforeDeadline") as EligibilityInput["applicationBeforeDeadline"],
      recentRegistrationOk: form.get("recentRegistrationOk") as EligibilityInput["recentRegistrationOk"],
      noOtherOngoingHouseholdCase: form.get("noOtherOngoingHouseholdCase") as EligibilityInput["noOtherOngoingHouseholdCase"],
      firstPremiumForApplicant: form.get("firstPremiumForApplicant") as EligibilityInput["firstPremiumForApplicant"],
      sameAddressFourMonths: form.get("sameAddressFourMonths") as EligibilityInput["sameAddressFourMonths"],
      eligibleAreaTwelveMonths: form.get("eligibleAreaTwelveMonths") as EligibilityInput["eligibleAreaTwelveMonths"],
      registeredForTwelveMonths: form.get("registeredForTwelveMonths") as EligibilityInput["registeredForTwelveMonths"],
      truthfulApplication: form.get("truthfulApplication") as EligibilityInput["truthfulApplication"],
      canProvideDocuments: form.get("canProvideDocuments") as EligibilityInput["canProvideDocuments"],
      acceptEuControls: form.get("acceptEuControls") as EligibilityInput["acceptEuControls"]
    };
}

function persistEligibilityForm(formElement: HTMLFormElement) {
  const values: Record<string, string | boolean> = {};
  Array.from(formElement.elements).forEach((element) => {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) return;
    if (!element.name) return;
    values[element.name] = element instanceof HTMLInputElement && element.type === "checkbox" ? element.checked : element.value;
  });
  localStorage.setItem(ELIGIBILITY_STORAGE_KEY, JSON.stringify(values));
}

function restoreEligibilityForm(formElement: HTMLFormElement) {
  const raw = localStorage.getItem(ELIGIBILITY_STORAGE_KEY);
  if (!raw) return;
  try {
    const values = JSON.parse(raw) as Record<string, string | boolean>;
    Object.entries(values).forEach(([name, value]) => {
      const element = formElement.elements.namedItem(name);
      if (element instanceof HTMLInputElement && element.type === "checkbox") element.checked = Boolean(value);
      if (element instanceof HTMLInputElement && element.type !== "checkbox") element.value = String(value);
      if (element instanceof HTMLSelectElement) element.value = String(value);
    });
  } catch {
    localStorage.removeItem(ELIGIBILITY_STORAGE_KEY);
  }
}

function updateTestLiveState(rules: RuleData, formElement: HTMLFormElement) {
  const input = readEligibilityForm(formElement);
  const completed = [
    input.inEligibleArea !== "unknown" && Boolean((formElement.elements.namedItem("address") as HTMLInputElement | null)?.value.trim()),
    input.householdIncome > 0,
    input.vehicleCost > 0,
    input.ageAndIdOk === "yes" && input.hasSwedishPersonalNumber === "yes"
  ];
  const count = completed.filter(Boolean).length;
  const progress = document.querySelector("#testProgress");
  const result = assessEligibility(input, rules);
  updateFieldsetStates(formElement);
  if (progress) {
    progress.className = `test-progress ${result.status}`;
    progress.innerHTML = `<strong>${count} av 4 delar klara · ${result.title}</strong><span>${nextStepText(completed, result.status)}</span>`;
  }
  renderAssessment(result);
}

function updateFieldsetStates(formElement: HTMLFormElement) {
  formElement.querySelectorAll("fieldset").forEach((fieldset) => {
    const controls = Array.from(fieldset.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input[name], select[name]"));
    controls.forEach((control) => applyControlState(control, formElement));
    const hasInvalid = controls.some((control) => control.dataset.state === "invalid");
    const hasWarning = controls.some((control) => control.dataset.state === "warning");
    const hasMissing = controls.some((control) => control.dataset.state === "missing");
    fieldset.dataset.state = hasInvalid ? "invalid" : hasWarning ? "warning" : hasMissing ? "missing" : "complete";
    let badge = fieldset.querySelector<HTMLElement>(".fieldset-status");
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "fieldset-status";
      fieldset.querySelector("legend")?.appendChild(badge);
    }
    badge.textContent = hasInvalid ? "Stoppar" : hasWarning ? "Kontrollera" : hasMissing ? "Saknas" : "Klar";
  });
}

function applyControlState(control: HTMLInputElement | HTMLSelectElement, formElement: HTMLFormElement) {
  const value = control instanceof HTMLInputElement && control.type === "checkbox" ? String(control.checked) : control.value;
  control.dataset.state = "complete";

  if (control.type === "hidden" && control.name === "inEligibleArea") {
    control.dataset.state = value === "yes" ? "complete" : value === "no" ? "invalid" : "missing";
    return;
  }

  if (control.name === "address") {
    const area = (formElement.elements.namedItem("inEligibleArea") as HTMLInputElement | null)?.value;
    control.dataset.state = !value.trim() ? "missing" : area === "yes" ? "complete" : area === "no" ? "invalid" : "warning";
    return;
  }

  if (control instanceof HTMLInputElement && control.type === "checkbox") {
    control.dataset.state = control.checked ? "complete" : "invalid";
    return;
  }
  if (value === "" || value === "unknown" || value === "0") {
    control.dataset.state = "missing";
    return;
  }

  if (control.name === "inEligibleArea" && value === "no") control.dataset.state = "invalid";
  if (control.name === "paysStateTax" && value === "yes") control.dataset.state = "invalid";
  if (control.name === "recentEvOrHybrid" && value === "yes") control.dataset.state = "invalid";
  if (control.name === "ageAndIdOk" && value === "no") control.dataset.state = "invalid";
  if (control.name === "hasSwedishPersonalNumber" && value === "no") control.dataset.state = "invalid";
  if (control.name === "protectedRegistration" && value === "yes") control.dataset.state = "invalid";

  const advancedYesRequired = new Set([
    "carIsElectricClass",
    "carIsRegisteredActive",
    "carNoPreviousPremium",
    "registeredOwnerOrHolder",
    "notOwnedByHouseholdRelatedCompany",
    "notBusinessUse",
    "applicationBeforeDeadline",
    "recentRegistrationOk",
    "noOtherOngoingHouseholdCase",
    "firstPremiumForApplicant",
    "sameAddressFourMonths",
    "eligibleAreaTwelveMonths",
    "registeredForTwelveMonths",
    "truthfulApplication",
    "canProvideDocuments",
    "acceptEuControls"
  ]);
  if (advancedYesRequired.has(control.name) && value === "no") control.dataset.state = "invalid";

  if (control.name === "vehicleCost") {
    const acquisition = (formElement.elements.namedItem("acquisition") as HTMLSelectElement | null)?.value;
    const cost = Number(value);
    const outOfPurchaseRange = acquisition === "purchase" && (cost < fallbackRules.purchase.minPrice || cost > fallbackRules.purchase.maxPrice);
    const outOfLeasingRange = acquisition === "leasing" && (cost < fallbackRules.leasing.minMonthlyCost || cost > fallbackRules.leasing.maxMonthlyCost);
    if (outOfPurchaseRange || outOfLeasingRange) control.dataset.state = "invalid";
  }

  if (control.name === "householdIncome") {
    const householdType = (formElement.elements.namedItem("householdType") as HTMLSelectElement | null)?.value || "";
    const limit = fallbackRules.incomeThresholds[householdType];
    if (limit && Number(value) >= limit) control.dataset.state = "invalid";
    else if (isIncomeStateTaxWarning(householdType, Number(value))) control.dataset.state = "warning";
  }
}

function isIncomeStateTaxWarning(householdType: string, householdIncome: number): boolean {
  if (!Number.isFinite(householdIncome) || householdIncome <= 0) return false;
  const adults = householdType.toLowerCase().startsWith("två") ? 2 : 1;
  if (adults === 1) return householdIncome > 660400;
  return householdIncome * 0.7 > 660400;
}

function nextStepText(completed: boolean[], status: string): string {
  if (!completed[0]) return "Börja med adressen. När kartan hittar området fylls platsfrågan automatiskt.";
  if (!completed[1]) return "Fyll i hushållsinkomst och skattefrågorna. De är avgörande för bedömningen.";
  if (!completed[2]) return "Fyll i pris eller månadskostnad.";
  if (!completed[3]) return "Svara på kontrollfrågorna för att minska osäkerheten.";
  return status === "troligen-ja" ? "Alla delar är ifyllda. Kontrollera ändå officiellt hos Naturvårdsverket." : "Alla delar är ifyllda. Läs motiveringen nedan.";
}

function renderAssessment(result: ReturnType<typeof assessEligibility>) {
  const target = document.querySelector("#assessment");
  if (!target) return;
  target.className = `assessment ${result.status}`;
  const cautionBlockers = result.cautions.filter((item) => item.startsWith("Kan stoppa ansökan:"));
  const missing = result.cautions.filter((item) => !item.startsWith("Kan stoppa ansökan:") && /saknas|kontrolleras|behöver/i.test(item));
  const good = result.reasons.filter((item) => !/inte|Ingen|får inte|måste/i.test(item));
  const blockers = result.reasons.filter((item) => /inte|Ingen|får inte|måste|utanför|saknar/i.test(item));
  const formElement = document.querySelector<HTMLFormElement>("#eligibilityForm");
  const premium = formElement && result.status !== "troligen-nej" ? estimatePremium(readEligibilityForm(formElement), fallbackRules) : undefined;
  target.innerHTML = `
    <h3>${result.title}</h3>
    ${premium ? `<div class="premium-estimate">
      <strong>Uppskattad premie</strong>
      <span>${formatMoney(premium.total)} totalt${premium.startBonus ? `, inklusive ${formatMoney(premium.startBonus)} i starttillägg` : ""}</span>
      <b>Starttillägg: ${premium.startBonusStatus}</b>
      <small>Utbetalning: ${formatMoney(fallbackRules.amounts.monthlyPremium)} per månad i högst ${fallbackRules.amounts.maxMonths} månader. Eventuellt starttillägg betalas som engångsbelopp vid första månadsutbetalningen.</small>
      <small>${premium.explanation}</small>
    </div>` : ""}
    ${good.length ? `<h4>Det här ser bra ut</h4><ul>${good.map((reason) => `<li>${reason}</li>`).join("")}</ul>` : ""}
    ${missing.length ? `<h4>Det här saknas eller behöver kontrolleras</h4><ul>${missing.map((reason) => `<li>${reason}</li>`).join("")}</ul>` : ""}
    ${blockers.length || cautionBlockers.length ? `<h4>Det här kan stoppa ansökan</h4><ul>${[...blockers, ...cautionBlockers].map((reason) => `<li>${reason.replace("Kan stoppa ansökan: ", "")}</li>`).join("")}</ul>` : ""}
    ${!missing.length && !blockers.length ? `<p><strong>Nästa steg:</strong> kontrollera uppgifterna i Naturvårdsverkets officiella test.</p>` : `<p><strong>Nästa steg:</strong> fyll i eller kontrollera punkterna ovan.</p>`}
  `;
}

function estimatePremium(input: EligibilityInput, rules: RuleData) {
  const startLimit = rules.startBonusThresholds[input.householdType];
  const hasStartBonus = Boolean(startLimit && input.householdIncome > 0 && input.householdIncome <= startLimit);
  const startBonusStatus = !startLimit || input.householdIncome <= 0
    ? "kan inte avgöras ännu"
    : hasStartBonus
      ? `ja, inkomsten ligger på eller under ${formatMoney(startLimit)}`
      : `nej, inkomsten ligger över ${formatMoney(startLimit)}`;
  const base = rules.amounts.totalPremium;
  const startBonus = hasStartBonus ? rules.amounts.startBonus : 0;
  return {
    total: base + startBonus,
    startBonus,
    startBonusStatus,
    explanation: hasStartBonus
      ? `SFS 2025:1574 18-19 §§: ansökan före 1 juli 2028 kan ge ${formatMoney(base)} plus starttillägg när hushållsinkomsten inte överstiger 50 %-gränsen (${formatMoney(startLimit)} för vald hushållstyp).`
      : startLimit && input.householdIncome > 0
        ? `SFS 2025:1574 19 §: grundpremien är ${formatMoney(base)} före 1 juli 2028. Starttillägget kräver högst ${formatMoney(startLimit)} för vald hushållstyp.`
        : `SFS 2025:1574 19 §: grundpremien är ${formatMoney(base)} före 1 juli 2028. Fyll i hushållsinkomst för att bedöma starttillägg.`
  };
}

function renderInfo(content: ContentData, rules: RuleData) {
  const target = document.querySelector("#info");
  if (!target) return;
  target.innerHTML = `
    <div class="section-title"><div><p class="eyebrow">Villkor och belopp</p><h2>Kort sammanfattning</h2></div><span class="badge">Hämtad ${content.retrievedAt}</span></div>
    <div class="info-grid">
      <article>
        <h3>Inofficiell hjälp</h3>
        <p>Den här sidan är inte Naturvårdsverkets officiella test och fattar inga beslut. Den sammanfattar öppna källor och gör en lokal, preliminär kontroll.</p>
        <p class="source">Använd alltid Naturvårdsverkets officiella test och ansökan för skarp bedömning.</p>
      </article>
      <article>
        <h3>Belopp och huvudvillkor</h3>
        <p>Premien är ${formatMoney(rules.amounts.monthlyPremium)} per månad i högst ${rules.amounts.maxMonths} månader, totalt ${formatMoney(rules.amounts.totalPremium)}. Hushåll under starttilläggsgränsen kan även få ${formatMoney(rules.amounts.startBonus)} vid första utbetalningen.</p>
        <p>Plats, hushållsinkomst, bilens pris eller månadskostnad och tidigare elbil eller laddhybrid i hushållet påverkar bedömningen.</p>
        <p class="source">Källa: <a href="${rules.sources[0]?.url}" target="_blank" rel="noreferrer">Naturvårdsverket</a></p>
      </article>
    </div>
  `;
}

function renderFaq(content: ContentData) {
  const target = document.querySelector("#faq");
  if (!target) return;
  target.innerHTML = `
    <div class="section-title"><div><p class="eyebrow">Omkodad i egna ord</p><h2>FAQ</h2></div></div>
    <div class="faq-list">
      ${content.faq.map((item) => `
        <details>
          <summary>${item.question}</summary>
          <p>${item.answer}</p>
          <p class="source">Källa: <a href="${item.sourceUrl}" target="_blank" rel="noreferrer">${new URL(item.sourceUrl).hostname}</a></p>
        </details>
      `).join("")}
    </div>
  `;
}
